import { useCallback, useEffect, useRef, useState } from 'react'

interface Options {
    /**
     * Se a página deve ACEITAR o arquivo solto.
     *
     * Recusar não devolve o comportamento do navegador: soltar continua sendo
     * neutralizado, apenas nada acontece. É a diferença entre "não fizemos
     * nada" e "o usuário errou o alvo por dois centímetros e perdeu a página
     * porque o navegador navegou para o arquivo".
     */
    accept: boolean
    /**
     * `targetFolderId` é a pasta sob o cursor no momento da soltura, ou `null`
     * quando o arquivo caiu em qualquer outro lugar da página.
     */
    onFiles: (
        files: File[],
        skippedFolders: number,
        targetFolderId: number | null,
    ) => void
}

/**
 * Atributo que marca um elemento como pasta de destino.
 *
 * A alternativa seria pendurar `onDragOver`/`onDrop` em cada card, mas aí o
 * `drop` do card precisaria interromper a propagação para a janela não enviar
 * de novo — e, interrompendo, o contador de profundidade do arraste nunca
 * zeraria e a sobreposição ficaria presa na tela. Com o atributo, o alvo sai do
 * elemento sob o cursor e existe UM só caminho de tratamento.
 */
export const DROP_FOLDER_ATTRIBUTE = 'data-drop-folder-id'

/** Pasta de destino sob o cursor, se houver. */
function findTargetFolderId(target: EventTarget | null): number | null {
    if (!(target instanceof Element)) return null

    const element = target.closest(`[${DROP_FOLDER_ATTRIBUTE}]`)
    if (!element) return null

    const raw = Number(element.getAttribute(DROP_FOLDER_ATTRIBUTE))
    return Number.isFinite(raw) ? raw : null
}

/**
 * O arraste só conta quando traz ARQUIVOS.
 *
 * Arrastar texto selecionado, link ou imagem de outra aba também dispara os
 * eventos de drag — sem esta checagem, a sobreposição de "solte aqui" apareceria
 * ao selecionar um nome de arquivo e arrastá-lo dois pixels.
 */
function carriesFiles(transfer: DataTransfer | null): boolean {
    if (!transfer) return false
    return Array.from(transfer.types ?? []).includes('Files')
}

/**
 * Separa o que dá para enviar do que não dá.
 *
 * Pasta arrastada chega como um item sem conteúdo útil: enviá-la criaria um
 * arquivo vazio com o nome da pasta. Melhor contar quantas foram ignoradas e
 * dizer isso ao usuário do que gravar lixo.
 */
function extractFiles(transfer: DataTransfer): {
    files: File[]
    skippedFolders: number
} {
    const files: File[] = []
    let skippedFolders = 0

    const items = Array.from(transfer.items ?? [])
    if (items.length) {
        for (const item of items) {
            if (item.kind !== 'file') continue

            const entry = item.webkitGetAsEntry?.()
            if (entry?.isDirectory) {
                skippedFolders++
                continue
            }

            const file = item.getAsFile()
            if (file) files.push(file)
        }
        return { files, skippedFolders }
    }

    // Navegador sem `DataTransferItemList`: sobra a lista crua, sem como
    // distinguir pasta de arquivo.
    return { files: Array.from(transfer.files ?? []), skippedFolders: 0 }
}

/**
 * Arrastar arquivo para QUALQUER ponto da página, e não só para dentro de um
 * campo específico — o comportamento que o Drive tem.
 *
 * Os ouvintes ficam na janela porque a área útil não cobre a tela inteira: com
 * eles presos a uma `div`, soltar o arquivo na margem, no cabeçalho ou sobre um
 * espaço vazio faria o navegador abrir o arquivo na aba e o usuário perderia a
 * página.
 */
export function usePageFileDrop({ accept, onFiles }: Options) {
    const [draggingFiles, setDraggingFiles] = useState(false)
    /** Pasta destacada sob o cursor. `null` = vai para a pasta aberta. */
    const [dropFolderId, setDropFolderId] = useState<number | null>(null)

    /**
     * Contador de profundidade, não booleano.
     *
     * `dragleave` dispara ao passar de um elemento para um filho dele. Com um
     * booleano, a sobreposição pisca a cada elemento que o cursor cruza; com o
     * contador, ela só some quando o arraste realmente deixa a janela.
     */
    const depthRef = useRef(0)

    const reset = useCallback(() => {
        depthRef.current = 0
        setDraggingFiles(false)
        setDropFolderId(null)
    }, [])

    useEffect(() => {
        if (!accept) reset()

        const onDragEnter = (event: DragEvent) => {
            if (!carriesFiles(event.dataTransfer)) return
            event.preventDefault()
            if (!accept) return
            depthRef.current++
            setDraggingFiles(true)
        }

        const onDragOver = (event: DragEvent) => {
            if (!carriesFiles(event.dataTransfer)) return
            // Sem o preventDefault aqui, o `drop` nunca chega: o padrão do
            // navegador é recusar a soltura e navegar para o arquivo.
            event.preventDefault()
            if (event.dataTransfer) {
                event.dataTransfer.dropEffect = accept ? 'copy' : 'none'
            }
            if (!accept) return

            // `dragover` dispara dezenas de vezes por segundo: só mexe no
            // estado quando a pasta sob o cursor realmente muda.
            const target = findTargetFolderId(event.target)
            setDropFolderId(current => (current === target ? current : target))
        }

        const onDragLeave = (event: DragEvent) => {
            if (!accept) return
            if (!carriesFiles(event.dataTransfer)) return
            depthRef.current = Math.max(0, depthRef.current - 1)
            if (depthRef.current === 0) setDraggingFiles(false)
        }

        const onDrop = (event: DragEvent) => {
            if (!carriesFiles(event.dataTransfer)) return
            // Neutraliza SEMPRE, aceitando ou não.
            event.preventDefault()
            if (!accept) return

            // O alvo sai daqui, e não do estado: entre o último `dragover` e a
            // soltura o cursor pode ter saído da pasta, e o React ainda não
            // teria repintado.
            const targetFolderId = findTargetFolderId(event.target)

            reset()
            const { files, skippedFolders } = extractFiles(event.dataTransfer!)
            if (files.length || skippedFolders) {
                onFiles(files, skippedFolders, targetFolderId)
            }
        }

        // O arraste pode terminar fora da janela (soltar na barra de tarefas,
        // ou apertar Esc). Sem isto, a sobreposição fica presa na tela.
        const onDragEnd = () => reset()
        const onWindowBlur = () => reset()

        window.addEventListener('dragenter', onDragEnter)
        window.addEventListener('dragover', onDragOver)
        window.addEventListener('dragleave', onDragLeave)
        window.addEventListener('drop', onDrop)
        window.addEventListener('dragend', onDragEnd)
        window.addEventListener('blur', onWindowBlur)

        return () => {
            window.removeEventListener('dragenter', onDragEnter)
            window.removeEventListener('dragover', onDragOver)
            window.removeEventListener('dragleave', onDragLeave)
            window.removeEventListener('drop', onDrop)
            window.removeEventListener('dragend', onDragEnd)
            window.removeEventListener('blur', onWindowBlur)
        }
    }, [accept, onFiles, reset])

    return { draggingFiles, dropFolderId }
}
